import {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { CookieConsentContext } from './cookieConsentContextShared';

const STORAGE_KEY = 'klagma_cookie_prefs';
const LEGACY_ACCEPT_KEY = 'klagma_cookie_consent';
const SCHEMA_V2 = 2;
const SCHEMA_V1 = 1;

/**
 * Browser storage notice (not third-party marketing/analytics).
 * `klagma:cookie-consent` fires when the user acknowledges; detail includes
 * `analyticsAllowed` / `marketingAllowed` (always false until you add gated scripts).
 */

function migrateLegacyAcceptString() {
  try {
    if (localStorage.getItem(LEGACY_ACCEPT_KEY) === 'accepted') {
      const payload = {
        v: SCHEMA_V2,
        acknowledgedAt: new Date().toISOString(),
      };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
      localStorage.removeItem(LEGACY_ACCEPT_KEY);
    }
  } catch {
    /* ignore */
  }
}

/** @returns {{ acknowledgedAt: string } | null} */
function loadState() {
  migrateLegacyAcceptString();
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);

    if (parsed?.v === SCHEMA_V2 && typeof parsed.acknowledgedAt === 'string') {
      return { acknowledgedAt: parsed.acknowledgedAt };
    }

    if (parsed?.v === SCHEMA_V1) {
      const acknowledgedAt =
        typeof parsed.consentAt === 'string'
          ? parsed.consentAt
          : new Date().toISOString();
      const next = { v: SCHEMA_V2, acknowledgedAt };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
      return { acknowledgedAt };
    }
  } catch {
    /* ignore */
  }
  return null;
}

function persistAcknowledged() {
  const acknowledgedAt = new Date().toISOString();
  localStorage.setItem(
    STORAGE_KEY,
    JSON.stringify({ v: SCHEMA_V2, acknowledgedAt })
  );
  return { acknowledgedAt };
}

function dispatchNoticeEvent(detail) {
  try {
    window.dispatchEvent(
      new CustomEvent('klagma:cookie-consent', { detail })
    );
  } catch {
    /* ignore */
  }
}

export function CookieConsentProvider({ children }) {
  const [state, setState] = useState(() =>
    typeof window !== 'undefined' ? loadState() : null
  );
  const [settingsOpen, setSettingsOpen] = useState(false);

  const hasAnswered = state !== null;

  const acknowledge = useCallback(() => {
    const { acknowledgedAt } = persistAcknowledged();
    setState({ acknowledgedAt });
    setSettingsOpen(false);
    dispatchNoticeEvent({
      v: SCHEMA_V2,
      acknowledgedAt,
      analyticsAllowed: false,
      marketingAllowed: false,
    });
  }, []);

  const openSettings = useCallback(() => setSettingsOpen(true), []);
  const closeSettings = useCallback(() => setSettingsOpen(false), []);

  const value = useMemo(
    () => ({
      hasAnswered,
      acknowledgedAt: state?.acknowledgedAt ?? null,
      /** No analytics scripts are loaded yet; keep false until you gate e.g. GA behind consent. */
      analyticsAllowed: false,
      /** No marketing pixels are loaded yet. */
      marketingAllowed: false,
      settingsOpen,
      acknowledge,
      openSettings,
      closeSettings,
    }),
    [hasAnswered, state?.acknowledgedAt, settingsOpen, acknowledge, openSettings, closeSettings]
  );

  useEffect(() => {
    if (state) {
      dispatchNoticeEvent({
        v: SCHEMA_V2,
        acknowledgedAt: state.acknowledgedAt,
        analyticsAllowed: false,
        marketingAllowed: false,
      });
    }
  }, [state]);

  return (
    <CookieConsentContext.Provider value={value}>
      {children}
    </CookieConsentContext.Provider>
  );
}
